'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { UtilityButton } from '../Button/UtilityButton'
import { MaximizeIcon } from '../Icon/MaximizeIcon'
import { MinimizeIcon } from '../Icon/MinimizeIcon'
import { RefreshIcon } from '../Icon/RefreshIcon'
import { ZoomInIcon } from '../Icon/ZoomInIcon'
import { ZoomOutIcon } from '../Icon/ZoomOutIcon'
import {
  FileViewerSize,
  fileViewerControlButtonStyles,
  fileViewerControlsRowStyles,
} from './FileViewer.styles'

type ZoomState = {
  canZoomIn: boolean
  canZoomOut: boolean
  isZoomed: boolean
  /** Zoom buttons only make sense on a zoomable (image, fullscreen) surface. */
  shouldShowZoomControls: boolean
}

type ZoomActions = {
  onZoomIn: () => void
  onZoomOut: () => void
  onReset: () => void
}

type FileViewerControlsProps = {
  size: FileViewerSize
  colorScheme: ColorScheme
  onToggleSize: () => void
  zoomState: ZoomState
  zoomActions: ZoomActions
}

/**
 * Control row floating over Modal's header: zoom out / reset / zoom in (image
 * at fullscreen only) and the size toggle. Close belongs to Modal's cat-ear
 * button, so there is none here. Replaces the web-app's Chakra v1
 * MediaPreviewerControls (ENG-32107); each control is a UtilityButton so the
 * hover tooltips come from the package Tooltip.
 */
export const FileViewerControls = ({
  size,
  colorScheme,
  onToggleSize,
  zoomState,
  zoomActions,
}: FileViewerControlsProps) => {
  const { canZoomIn, canZoomOut, isZoomed, shouldShowZoomControls } = zoomState
  const isNormalSize = size === 'normal'
  const buttonStyles = fileViewerControlButtonStyles(colorScheme)

  return (
    <Box {...fileViewerControlsRowStyles}>
      {shouldShowZoomControls && (
        <>
          <UtilityButton
            colorScheme={colorScheme}
            disabled={!canZoomOut}
            icon={<ZoomOutIcon />}
            label='Zoom out'
            onClick={zoomActions.onZoomOut}
            stylesOverride={buttonStyles}
            tooltipLabel='Zoom out'
          />
          <UtilityButton
            colorScheme={colorScheme}
            disabled={!isZoomed}
            icon={<RefreshIcon />}
            label='Reset zoom'
            onClick={zoomActions.onReset}
            stylesOverride={buttonStyles}
            tooltipLabel='Reset zoom'
          />
          <UtilityButton
            colorScheme={colorScheme}
            disabled={!canZoomIn}
            icon={<ZoomInIcon />}
            label='Zoom in'
            onClick={zoomActions.onZoomIn}
            stylesOverride={buttonStyles}
            tooltipLabel='Zoom in'
          />
        </>
      )}
      <UtilityButton
        colorScheme={colorScheme}
        icon={isNormalSize ? <MaximizeIcon /> : <MinimizeIcon />}
        label={isNormalSize ? 'Expand' : 'Collapse'}
        onClick={onToggleSize}
        stylesOverride={buttonStyles}
        tooltipLabel={
          isNormalSize ? 'Expand to full screen' : 'Collapse to normal size'
        }
      />
    </Box>
  )
}
